import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { Informe } from "@/lib/informes";

import { formatearDecimal } from "./formato";

/** Los tres tipos de línea que existen. Espejo de `TipoDeLinea` en el API. */
export type TipoDeLinea = "tasacion" | "gastos" | "descuento";

/**
 * Lo que el ejecutivo COMPLETA del cuadro: el honorario del informe, un gasto
 * y un descuento.
 *
 * UN SOLO honorario, no uno por fila del "Detalle inmuebles tasación": desde
 * que el informe volvió al árbol plano `informes → informe_roles`, ONE informe
 * = ONE tasación. Antes había una línea por cada `informe_propiedades`, y ese
 * nivel era maquinaria para el caso incorrecto de la 22160 (en realidad cinco
 * tasaciones distintas metidas en un documento) — no una regla general.
 */
export type CuadroEditable = {
  tasacion: string;
  gastos: string;
  /** El valor escrito: UF o %, según `descuentoModo`. */
  descuento: string;
  descuentoModo: ModoDelDescuento;
};

/**
 * En qué unidad se escribe el descuento. En `porcentaje` el servidor deriva el
 * monto de los honorarios, así que un cambio de honorario arrastra el monto y
 * el "10 %" sigue siendo 10 %.
 */
export type ModoDelDescuento = "uf" | "porcentaje";

/** Una línea ya armada, lista para viajar al API. */
export type LineaDelCuadro = {
  tipo: TipoDeLinea;
  concepto: string;
  monto_uf: number;
  /** Solo un descuento pactado en porcentaje; en UF la clave ni viaja. */
  porcentaje?: number;
  orden: number;
};

const CONCEPTO_GASTOS = "Gastos Generales";
const CONCEPTO_DESCUENTO = "Descuento";

export function aNumero(valor: string): number {
  const numero = Number(valor.replace(",", "."));
  return Number.isFinite(numero) ? numero : 0;
}

/**
 * El concepto de la ÚNICA línea de tasación, que es lo que IMPRIME el
 * documento: el nombre de la propiedad del informe y, si todavía no se
 * escribió, sus roles — nunca en blanco, porque el papel no puede mostrar una
 * línea de honorarios sin decir de qué habla.
 *
 * NO es el rótulo del campo en pantalla. Ver el `<Label>` del cuadro: ahí el
 * nombre de la propiedad no va.
 */
export function conceptoDeTasacion(informe: Informe): string {
  return (
    informe.propiedad_nombre ||
    (informe.roles ?? []).map((rol) => rol.rol_sii).join(" y ") ||
    "Tasación"
  );
}

/**
 * El descuento en UF, sea cual sea la unidad en que se escribió.
 *
 * En porcentaje la base son los HONORARIOS y no los gastos (así lo calcula la
 * 22190), redondeado a dos decimales. Gemelo del cálculo de
 * `Informe::cotizar()`: el servidor recalcula y esto solo lo muestra en vivo.
 */
export function descuentoEnUf(cuadro: CuadroEditable): number {
  const escrito = aNumero(cuadro.descuento);
  if (cuadro.descuentoModo !== "porcentaje") return escrito;

  return Math.round(aNumero(cuadro.tasacion) * escrito) / 100;
}

/**
 * Las líneas que viajan al API, armadas del cuadro.
 *
 * Gastos y descuento se OMITEN cuando quedan vacíos o en cero: una fila de
 * "Gastos Generales UF 0" en el documento dice que hubo gastos y que fueron
 * nada, que no es lo mismo que no haberlos tenido.
 */
export function lineasDelCuadro(cuadro: CuadroEditable, concepto: string): LineaDelCuadro[] {
  const lineas: LineaDelCuadro[] = [
    { tipo: "tasacion", concepto, monto_uf: aNumero(cuadro.tasacion), orden: 1 },
  ];

  let orden = 1;

  const gastos = aNumero(cuadro.gastos);
  if (gastos > 0) {
    orden++;
    lineas.push({ tipo: "gastos", concepto: CONCEPTO_GASTOS, monto_uf: gastos, orden });
  }

  const descuento = descuentoEnUf(cuadro);
  if (descuento > 0) {
    orden++;
    lineas.push({
      tipo: "descuento",
      concepto: CONCEPTO_DESCUENTO,
      monto_uf: descuento,
      // El monto viaja igual (el servidor lo recalcula y descarta este): así la
      // línea es la misma que se ve en pantalla.
      ...(cuadro.descuentoModo === "porcentaje" ? { porcentaje: aNumero(cuadro.descuento) } : {}),
      orden,
    });
  }

  return lineas;
}

/** El total neto del cuadro: la tasación más los gastos, menos el descuento. */
export function totalDelCuadro(cuadro: CuadroEditable): number {
  // El concepto no importa para el total, así que se pasa vacío: es un
  // detalle interno de `lineasDelCuadro` que acá no hace falta resolver.
  const suma = lineasDelCuadro(cuadro, "").reduce(
    (acumulado, linea) =>
      acumulado + (linea.tipo === "descuento" ? -linea.monto_uf : linea.monto_uf),
    0,
  );

  // Se redondea al CERRAR y no por línea: sumar montos ya redondeados arrastra
  // el error que se quiere evitar. Gemelo de `Cotizacion::totalUf()`.
  return Math.round(suma * 100) / 100;
}

/**
 * El cuadro de honorarios de la cotización.
 *
 * UNA SOLA LÍNEA DE TASACIÓN, la del informe entero: ya no se pide un
 * honorario por cada inmueble del "Detalle inmuebles tasación", porque ese
 * nivel dejó de existir. Gastos y descuento siguen siendo uno solo cada uno,
 * como ya lo eran: en las cinco cotizaciones reales de Consultek nunca hay más
 * de un gasto ni más de un descuento.
 *
 * Componente CONTROLADO: no guarda estado propio.
 */
export function CuadroDeHonorarios({
  cuadro,
  onChange,
  error,
}: {
  cuadro: CuadroEditable;
  onChange: (cuadro: CuadroEditable) => void;
  error?: string;
}) {
  const total = totalDelCuadro(cuadro);
  const enPorcentaje = cuadro.descuentoModo === "porcentaje";
  const descuentoUf = descuentoEnUf(cuadro);

  function cambiarModo(modo: ModoDelDescuento) {
    if (modo !== cuadro.descuentoModo) onChange({ ...cuadro, descuento: "", descuentoModo: modo });
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Label>Cuadro de honorarios</Label>
        {/* No es decoración: este número se imprime como "entrega de 1 Informe
            de Tasación", y desde el árbol plano SIEMPRE es uno — un informe
            YA ES una sola tasación. `Cotizacion::informesComprometidos()`
            sigue contando líneas para no contradecir una cotización emitida
            ANTES de este cambio, que puede tener varias. */}
        <p className="text-ink-hint text-caption">Compromete 1 informe de tasación</p>
      </div>

      <div className="space-y-2">
        {/* SIN el nombre de la propiedad, que sí va en el documento. En un
            rótulo de formulario "Honorarios de Packing y Bodega de
            Vinificación Macaya" se lee como los honorarios DE ese inmueble, y
            además tapa el campo con un nombre que la pantalla ya muestra en
            otra parte. Los tres campos del cuadro se rotulan igual de corto:
            Honorarios, Gastos generales, Descuento. */}
        <Label htmlFor="honorario-tasacion">Honorarios (UF)</Label>
        <Input
          id="honorario-tasacion"
          inputMode="decimal"
          className="max-w-40"
          placeholder="Ej: 76"
          value={cuadro.tasacion}
          onChange={(evento) => onChange({ ...cuadro, tasacion: evento.target.value })}
        />
      </div>

      {error ? <p className="text-destructive text-caption">{error}</p> : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="gastos-generales">Gastos generales (UF)</Label>
          <Input
            id="gastos-generales"
            inputMode="decimal"
            placeholder="Vacío = sin gastos asociados"
            value={cuadro.gastos}
            onChange={(evento) => onChange({ ...cuadro, gastos: evento.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="descuento">
            {enPorcentaje ? "Descuento (%)" : "Descuento (UF)"}
          </Label>
          {/* El selector va AL LADO del campo y no junto al rótulo: sus botones
              son más altos que un rótulo, y arriba empujaban el campo por
              debajo del de gastos, que está en la misma fila. */}
          <div className="flex items-center gap-2">
            <Input
              id="descuento"
              inputMode="decimal"
              className="flex-1"
              // Positivo, como en la base: el signo lo pone el tipo de la línea,
              // para que una fila no pueda significar lo contrario de su rótulo.
              placeholder="Vacío = sin descuento"
              value={cuadro.descuento}
              onChange={(evento) => onChange({ ...cuadro, descuento: evento.target.value })}
            />
            {/* Botones del DS con `aria-pressed` y no el `ToggleGroup` de
                shadcn: mismo criterio que `SelectorDeCapa`. Al cambiar de
                unidad se vacía el valor, porque "15" no significa lo mismo en
                UF que en %. */}
            <div className="flex shrink-0 items-center gap-0.5" role="group" aria-label="Unidad del descuento">
              <Button
                type="button"
                size="sm"
                variant={enPorcentaje ? "ghost" : "soft"}
                aria-pressed={!enPorcentaje}
                aria-label="Descuento en UF"
                onClick={() => cambiarModo("uf")}
              >
                UF
              </Button>
              <Button
                type="button"
                size="sm"
                variant={enPorcentaje ? "soft" : "ghost"}
                aria-pressed={enPorcentaje}
                aria-label="Descuento en porcentaje"
                onClick={() => cambiarModo("porcentaje")}
              >
                %
              </Button>
            </div>
          </div>
          {/* Con un porcentaje escrito SIEMPRE se dice sobre qué se calcula:
              la base son solo los honorarios, y sin ellos el descuento da
              cero — callar ahí hacía parecer que el porcentaje se ignoraba. */}
          {enPorcentaje && aNumero(cuadro.descuento) > 0 ? (
            <p className="text-ink-hint text-caption" data-testid="descuento-en-uf">
              {aNumero(cuadro.tasacion) > 0
                ? `Equivale a UF ${formatearDecimal(String(descuentoUf))} sobre los honorarios`
                : "Se calcula sobre los honorarios: ingrésalos para ver el monto"}
            </p>
          ) : null}
        </div>
      </div>

      <p className="text-ink text-h3 text-right">
        Total{" "}
        <span className="font-mono" data-testid="total-honorarios">
          UF {formatearDecimal(String(total)) ?? "0"}
        </span>{" "}
        <span className="text-ink-hint text-caption">+ IVA</span>
      </p>
    </div>
  );
}
